import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

const PORTAL_STORAGE_KEYS = {
  admin: {
    access: 'karali_admin_access_token',
    refresh: 'karali_admin_refresh_token',
  },
  staff: {
    access: 'karali_staff_access_token',
    refresh: 'karali_staff_refresh_token',
  },
};

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}

function getPortalTokens(scope) {
  const storage = getStorage();
  const keys = PORTAL_STORAGE_KEYS[scope];

  if (!storage || !keys) {
    return { accessToken: '', refreshToken: '' };
  }

  return {
    accessToken: storage.getItem(keys.access) || '',
    refreshToken: storage.getItem(keys.refresh) || '',
  };
}

function setPortalTokens(scope, accessToken, refreshToken) {
  const storage = getStorage();
  const keys = PORTAL_STORAGE_KEYS[scope];

  if (!storage || !keys) {
    return;
  }

  if (accessToken) {
    storage.setItem(keys.access, accessToken);
  }

  if (refreshToken) {
    storage.setItem(keys.refresh, refreshToken);
  }
}

export function clearPortalTokens(scope) {
  const storage = getStorage();
  const keys = PORTAL_STORAGE_KEYS[scope];

  if (!storage || !keys) {
    return;
  }

  storage.removeItem(keys.access);
  storage.removeItem(keys.refresh);
}

function persistPortalTokens(scope, payload) {
  if (!payload || typeof payload !== 'object') {
    return;
  }

  const accessToken =
    typeof payload.accessToken === 'string'
      ? payload.accessToken
      : typeof payload.tokens?.accessToken === 'string'
        ? payload.tokens.accessToken
        : '';
  const refreshToken =
    typeof payload.refreshToken === 'string'
      ? payload.refreshToken
      : typeof payload.tokens?.refreshToken === 'string'
        ? payload.tokens.refreshToken
        : '';

  if (accessToken || refreshToken) {
    setPortalTokens(scope, accessToken, refreshToken);
  }
}

function unwrapSuccessEnvelope(response) {
  if (
    response.data &&
    typeof response.data === 'object' &&
    response.data.success === true &&
    Object.prototype.hasOwnProperty.call(response.data, 'data')
  ) {
    response.data = response.data.data;
  }

  return response;
}

function normalizeApiError(error) {
  const apiMessage = error.response?.data?.error?.message || error.response?.data?.message;

  if (apiMessage) {
    error.message = Array.isArray(apiMessage) ? apiMessage[0] : apiMessage;
  }

  return Promise.reject(error);
}

function createPortalClient(scope, refreshPath) {
  const client = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 15000,
  });

  const refreshClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 15000,
  });

  let refreshPromise = null;

  client.interceptors.request.use((config) => {
    const { accessToken } = getPortalTokens(scope);

    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const unwrapped = unwrapSuccessEnvelope(response);
      persistPortalTokens(scope, unwrapped.data);
      return unwrapped;
    },
    async (error) => {
      const config = error.config;
      normalizeApiError(error).catch(() => undefined);

      if (
        refreshPath &&
        error.response?.status === 401 &&
        config &&
        !config._retry &&
        !String(config.url || '').includes(refreshPath)
      ) {
        config._retry = true;

        refreshPromise =
          refreshPromise ||
          refreshClient
            .post(refreshPath, { refreshToken: getPortalTokens(scope).refreshToken || undefined })
            .then((response) => {
              const unwrapped = unwrapSuccessEnvelope(response);
              persistPortalTokens(scope, unwrapped.data);
            })
            .catch((refreshError) => {
              clearPortalTokens(scope);
              throw refreshError;
            })
            .finally(() => {
              refreshPromise = null;
            });

        try {
          await refreshPromise;
          return client(config);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export const adminApi = createPortalClient('admin', '/admin/auth/refresh');
export const staffApi = createPortalClient('staff', '/staff/auth/refresh');
export const publicApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 15000,
});
publicApi.interceptors.response.use(unwrapSuccessEnvelope, normalizeApiError);
export { API_BASE_URL };
